import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface TabItem {
  id: string;
  title: string;
  path: string;
  isActive: boolean;
}

interface TabStore {
  tabs: TabItem[];
  activeTabId: string | null;
  addTab: (tab: Omit<TabItem, "id" | "isActive">) => void;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  moveTab: (fromIndex: number, toIndex: number) => void;
  clearAllTabs: () => void;
  getTabByPath: (path: string) => TabItem | undefined;
  closeOtherTabs: (id: string) => void;
  closeTabsToRight: (id: string) => void;
  closeTabsToLeft: (id: string) => void;
}

export const useTabStore = create<TabStore>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,

      addTab: tab => {
        const { tabs } = get();
        const existingTab = tabs.find(t => t.path === tab.path);

        if (existingTab) {
          // 이미 존재하는 탭이면 활성화만
          set({
            tabs: tabs.map(t => ({ ...t, isActive: t.id === existingTab.id })),
            activeTabId: existingTab.id,
          });
        } else {
          // 새 탭 추가
          const newTab: TabItem = {
            id: Date.now().toString(),
            title: tab.title,
            path: tab.path,
            isActive: true,
          };

          set({
            tabs: tabs.map(t => ({ ...t, isActive: false })).concat(newTab),
            activeTabId: newTab.id,
          });
        }
      },

      removeTab: id => {
        const { tabs, activeTabId } = get();
        const tabIndex = tabs.findIndex(t => t.id === id);
        const newTabs = tabs.filter(t => t.id !== id);

        let newActiveId = activeTabId;
        if (activeTabId === id && newTabs.length > 0) {
          // 활성 탭이 삭제되면 다음 탭을 활성화, 다음 탭이 없으면 이전 탭을 활성화
          let nextActiveTab;
          if (tabIndex < newTabs.length) {
            // 다음 탭이 있는 경우 (현재 탭 오른쪽)
            nextActiveTab = newTabs[tabIndex];
          } else {
            // 다음 탭이 없는 경우 이전 탭을 활성화 (현재 탭 왼쪽)
            nextActiveTab = newTabs[newTabs.length - 1];
          }

          newActiveId = nextActiveTab.id;
          newTabs.forEach(tab => {
            tab.isActive = tab.id === nextActiveTab.id;
          });
        }

        set({
          tabs: newTabs,
          activeTabId: newActiveId,
        });
      },

      setActiveTab: id => {
        const { tabs } = get();
        set({
          tabs: tabs.map(t => ({ ...t, isActive: t.id === id })),
          activeTabId: id,
        });
      },

      moveTab: (fromIndex, toIndex) => {
        const { tabs } = get();
        if (fromIndex === toIndex) return;
        if (fromIndex < 0 || fromIndex >= tabs.length) return;
        if (toIndex < 0 || toIndex >= tabs.length) return;

        const newTabs = [...tabs];
        const [movedTab] = newTabs.splice(fromIndex, 1);
        newTabs.splice(toIndex, 0, movedTab);

        set({ tabs: newTabs });
      },

      clearAllTabs: () => {
        set({ tabs: [], activeTabId: null });
      },

      getTabByPath: path => {
        const { tabs } = get();
        return tabs.find(t => t.path === path);
      },

      closeOtherTabs: id => {
        const { tabs } = get();
        const targetTab = tabs.find(t => t.id === id);
        if (targetTab) {
          set({
            tabs: [{ ...targetTab, isActive: true }],
            activeTabId: id,
          });
        }
      },

      closeTabsToRight: id => {
        const { tabs, activeTabId } = get();
        const targetIndex = tabs.findIndex(t => t.id === id);
        if (targetIndex === -1) return;

        const newTabs = tabs.slice(0, targetIndex + 1);
        // 활성 탭이 삭제되는 경우 대상 탭을 활성화
        const activeTabRemoved = !newTabs.find(t => t.id === activeTabId);
        if (activeTabRemoved) {
          newTabs.forEach(t => (t.isActive = t.id === id));
          set({ tabs: newTabs, activeTabId: id });
        } else {
          set({ tabs: newTabs });
        }
      },

      closeTabsToLeft: id => {
        const { tabs, activeTabId } = get();
        const targetIndex = tabs.findIndex(t => t.id === id);
        if (targetIndex === -1) return;

        const newTabs = tabs.slice(targetIndex);
        // 활성 탭이 삭제되는 경우 대상 탭을 활성화
        const activeTabRemoved = !newTabs.find(t => t.id === activeTabId);
        if (activeTabRemoved) {
          newTabs.forEach(t => (t.isActive = t.id === id));
          set({ tabs: newTabs, activeTabId: id });
        } else {
          set({ tabs: newTabs });
        }
      },
    }),
    {
      name: "tab-history",
      partialize: state => ({ tabs: state.tabs, activeTabId: state.activeTabId }),
    }
  )
);
